import { getHrAnalyticsOptions } from '@gozoki/api'
import { frenchDaysOfWeek } from '@gozoki/api-types'
import { typedObjectEntries } from '@gozoki/tools'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { AppSelect } from '~/components/new/app-ui/app-select'
import { StatCard } from '~/components/new/app-ui/stat-card'

export const periods = ['7-days', '30-days', '6-months', '1-year'] as const
type Period = (typeof periods)[number]

const periodDateRanges: Record<Period, () => { start: string; end: string }> = {
  '30-days': () => ({
    start: DateTime.now().minus({ days: 30 }).toISO(),
    end: DateTime.now().toISO(),
  }),
  '6-months': () => ({
    start: DateTime.now().minus({ months: 6 }).toISO(),
    end: DateTime.now().toISO(),
  }),
  '1-year': () => ({
    start: DateTime.now().minus({ years: 1 }).toISO(),
    end: DateTime.now().toISO(),
  }),
  '7-days': () => ({
    start: DateTime.now().minus({ days: 7 }).toISO(),
    end: DateTime.now().toISO(),
  }),
}

const periodsOptions = [
  { label: '7 jours', value: '7-days' },
  { label: '30 jours', value: '30-days' },
  { label: '6 mois', value: '6-months' },
  { label: '1 an', value: '1-year' },
] as const satisfies { label: string; value: Period }[]

export const PlanningDashboard = () => {
  const navigate = useNavigate({ from: '/hr/planning/$tab' })
  const { period = '7-days', storeId } = useSearch({ from: '/_authenticated/hr/planning/$tab' })

  const { start, end } = periodDateRanges[period]()

  const { data: hrAnalytics } = useQuery(
    getHrAnalyticsOptions({ storeId, dateStart: start, dateEnd: end }),
  )

  if (!hrAnalytics) return null

  return (
    <div className="flex flex-col gap-4">
      <AppSelect
        className="w-52"
        options={periodsOptions}
        value={period}
        onChange={(v) => navigate({ search: (prev) => ({ ...prev, period: v ?? undefined }) })}
      />

      <div className="grid grid-cols-3 gap-4">
        <StatCard
          title="EFFECTIFS"
          value={{ amount: hrAnalytics.headcount.total }}
          chart={{
            type: 'pie',
            data: [
              { label: 'Présentiel', value: hrAnalytics.headcount.onSite, fill: 'var(--chart-2)' },
              { label: 'Distanciel', value: hrAnalytics.headcount.remote, fill: 'var(--chart-1)' },
            ],
          }}
        />

        <StatCard
          title="MASSE SALARIALE"
          value={{ amount: hrAnalytics.salaryCost.amount, unit: '€' }}
          chart={{
            xLabelFormatter: (value) =>
              DateTime.fromISO(value).toLocaleString({ day: 'numeric', month: 'short' }),
            type: 'area',
            xAxisKey: 'date',
            yAxisKey: 'amount',
            data: hrAnalytics.salaryCost.histogram ?? [],
            tooltipLabel: 'Masse salariale',
            tooltipLabelFormatter: (value) =>
              DateTime.fromISO(value).toLocaleString({ day: 'numeric', month: 'long' }),
          }}
        />

        <StatCard
          title="HEURES TRAVAILLÉES"
          value={{ amount: hrAnalytics.workedHours.averagePerWeek, unit: 'h' }}
          chart={{
            type: 'bar',
            xAxisKey: 'day',
            yAxisKey: 'hoursWorked',
            data: typedObjectEntries(hrAnalytics.workedHours.averagePerDay).map(([day, hours]) => ({
              day: frenchDaysOfWeek[day],
              hoursWorked: hours,
            })),
            tooltipLabel: 'Heures travaillées',
            xLabelFormatter: (v) => v.charAt(0),
          }}
        />
      </div>
    </div>
  )
}
